import { useState } from "react";
import { ShieldCheck, Lock, Pencil, Check, Target } from "lucide-react";
import client from "../lib/api";
import { MoodPicker } from "./MoodPicker";
import { MOOD_BY_KEY } from "./moods";
import "./moods.css";

// The check-in composer from the redesign: a warm card with starter chips,
// one honest sentence, and the twelve animated mood faces. Owns its own
// draft state and posts to /engagement/reflection (kind "daily"), then
// hands the saved note back through onSaved so the surrounding screen can
// refresh its list, streak, and goal nudge. A mood is optional and never
// scored. `compact` drops the chips and the goal toggle for the quick-log
// popup.
const DEFAULT_CHIPS = [
  { label: "today I felt", text: "Today I felt " },
  { label: "one thing that went okay", text: "One thing that went okay " },
  { label: "on my mind rn", text: "On my mind right now " },
  { label: "a small win", text: "A small win " },
];

export default function DailyCheckin({
  onSaved,
  onError,
  chips = DEFAULT_CHIPS,
  showMakeGoal = true,
  accent = true,
  compact = false,
  bare = false,
  placeholder = "one honest sentence is enough",
  textareaTestid = "checkin-note",
  saveTestid = "checkin-save",
  moodTestid = "checkin-mood",
  rootTestid = "daily-checkin",
}) {
  const [note, setNote] = useState("");
  const [mood, setMood] = useState(null);
  const [makeGoal, setMakeGoal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const active = mood ? MOOD_BY_KEY[mood] : null;

  const applyChip = (text) => {
    setSaved(false);
    setNote((cur) => (cur.trim() === "" ? text : cur));
  };

  const save = async () => {
    if (!note.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await client.post("/engagement/reflection", {
        kind: "daily", text: note.trim(), make_goal: makeGoal, mood,
      });
      setSaved(true);
      setNote("");
      setMood(null);
      setMakeGoal(false);
      if (onSaved) onSaved(data);
      setTimeout(() => setSaved(false), 2200);
    } catch (_e) {
      if (onError) onError();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`mf-composer ${bare ? "mf-bare" : ""}`} data-testid={rootTestid}>
      {accent && !bare && <div className="mf-accent" />}
      <div className="mf-composer-head">
        <span className="mf-kicker">A new note</span>
        <span className="mf-privtag"><ShieldCheck size={14} /> private to you</span>
      </div>

      {!compact && chips.length > 0 && (
        <div className="mf-chips" data-testid={`${rootTestid}-chips`}>
          {chips.map((c, i) => (
            <button
              key={i}
              type="button"
              className="mf-chip"
              onClick={() => applyChip(c.text)}
              data-testid={`${rootTestid}-chip-${i}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <textarea
        className="mf-textarea"
        value={note}
        maxLength={800}
        placeholder={placeholder}
        onChange={(e) => { setNote(e.target.value); setSaved(false); }}
        data-testid={textareaTestid}
        style={compact ? { minHeight: 84, fontSize: 16 } : undefined}
      />

      <div className="mf-moodhead">
        <span className="mf-moodhead-label">how did today feel?</span>
        <span className="mf-echo" data-testid={`${rootTestid}-echo`}>{active ? active.echo : ""}</span>
      </div>
      <MoodPicker value={mood} onChange={(k) => { setMood(k); setSaved(false); }} testid={moodTestid} />

      {showMakeGoal && !compact && (
        <label className="mf-goal" data-testid={`${rootTestid}-makegoal`}>
          <span className={`mf-goal-track ${makeGoal ? "on" : ""}`}><span className="mf-goal-knob" /></span>
          <input
            type="checkbox"
            className="sr-only"
            checked={makeGoal}
            onChange={(e) => setMakeGoal(e.target.checked)}
            data-testid={`${rootTestid}-makegoal-input`}
          />
          <span className="mf-goal-text"><Target size={13} /> Make this a goal</span>
        </label>
      )}

      <div className="mf-foot">
        <span className="mf-reader"><Lock size={15} /> one reader: you</span>
        <button
          type="button"
          className={`mf-save ${saved ? "is-saved" : ""}`}
          onClick={save}
          disabled={!note.trim() || busy}
          data-testid={saveTestid}
        >
          {saved
            ? <><Check size={18} /> Saved</>
            : <><Pencil size={18} /> {busy ? "Saving" : "Save note"}</>}
        </button>
      </div>
    </div>
  );
}
